import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { getRuntimeEnvValue } from '../config/runtimeConfig';

// For browser requests, use /api which nginx will proxy to the backend
// For Docker server-side, use http://app:8081
function getEnvValue(key: string): string | undefined {
  return getRuntimeEnvValue(key);
}

function normalizeApiBaseUrl(rawBaseUrl?: string): string {
  const trimmed = (rawBaseUrl || '/api').trim().replace(/\/+$/, '');

  if (trimmed.endsWith('/api/v1')) {
    return trimmed.slice(0, -3);
  }

  if (trimmed.endsWith('/api')) {
    return trimmed;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return `${trimmed}/api`;
  }

  return trimmed || '/api';
}

function getBaseUrl(): string {
  return normalizeApiBaseUrl(getEnvValue('VITE_API_BASE_URL'));
}

function getServiceUsername(): string | undefined {
  return getEnvValue('VITE_SERVICE_USERNAME');
}

function getServicePassword(): string | undefined {
  return getEnvValue('VITE_SERVICE_PASSWORD');
}

function getConfiguredApiKey(): string | undefined {
  return getEnvValue('VITE_API_KEY');
}

export type FrontendAuthMode = 'local' | 'hybrid' | 'entra';

export function getFrontendAuthMode(): FrontendAuthMode {
  const raw = (getEnvValue('VITE_AUTH_MODE') || 'local').trim().toLowerCase();
  if (raw === 'hybrid' || raw === 'entra') {
    return raw;
  }
  return 'local';
}

let apiClient: AxiosInstance;
let initPromise: Promise<void> | null = null;
let refreshPromise: Promise<string | null> | null = null;
let refreshTimeout: ReturnType<typeof setTimeout> | null = null;

export function initializeApiClient(token?: string): AxiosInstance {
  const apiKey = getConfiguredApiKey();
  apiClient = axios.create({
    baseURL: `${getBaseUrl()}/v1`,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
      ...(apiKey ? { 'X-API-Key': apiKey } : {}),
    },
  });

  // Add auth token to all requests if available
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  // Interceptor to add token from session if available
  apiClient.interceptors.request.use((config) => {
    const session = getSessionToken();
    if (!config.headers['Authorization']) {
      if (session) {
        config.headers['Authorization'] = `Bearer ${session}`;
      }
    }
    return config;
  });

  // Interceptor to handle token refresh and errors
  apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
      const requestUrl = originalRequest?.url || '';
      const isAuthEndpoint = requestUrl.includes('/auth/login')
        || requestUrl.includes('/auth/refresh')
        || requestUrl.includes('/auth/entra/exchange');

      if (error.response?.status === 401 && !isAuthEndpoint && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const newToken = await refreshAccessToken();
          if (newToken) {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return apiClient(originalRequest);
          }
        } catch (refreshErr) {
          console.error('Refresh failed after 401:', refreshErr);
        }

        clearSessionToken();
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }

      return Promise.reject(error);
    },
  );

  return apiClient;
}

export function getApiClient(): AxiosInstance {
  if (!apiClient) {
    initializeApiClient();
  }
  return apiClient;
}

/** API key for raw fetch calls (e.g. /inventory/stats). Same as axios client X-API-Key. */
export function getApiKey(): string {
  return getConfiguredApiKey() || '';
}

export function setAuthToken(token: string): void {
  const client = getApiClient();
  client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

export function clearAuthToken(): void {
  const client = getApiClient();
  delete client.defaults.headers.common['Authorization'];
}

// Session token management (in-memory + optional cookie bridge)
const SESSION_TOKEN_KEY = 'tcg_session_token';
const SESSION_USER_KEY = 'tcg_session_user';

export interface SessionData {
  token: string;
  userId: string;
  username: string;
  expiresAt: number;
  authProvider?: 'local' | 'entra';
  refreshToken?: string;
  refreshExpiresAt?: number;
}

export function setSessionToken(data: SessionData): void {
  sessionStorage.setItem(SESSION_TOKEN_KEY, data.token);
  sessionStorage.setItem(SESSION_USER_KEY, JSON.stringify({
    userId: data.userId,
    username: data.username,
    expiresAt: data.expiresAt,
    authProvider: data.authProvider,
    refreshToken: data.refreshToken,
    refreshExpiresAt: data.refreshExpiresAt,
  }));
  // Bridge for legacy fetch clients that read from localStorage
  localStorage.setItem('access_token', data.token);
  if (data.refreshToken) {
    localStorage.setItem('refresh_token', data.refreshToken);
  } else {
    // Avoid reusing stale refresh tokens from previous sessions.
    localStorage.removeItem('refresh_token');
  }
  setAuthToken(data.token);

  // Schedule proactive refresh
  scheduleRefresh(data);
}

export function getSessionToken(): string | null {
  return sessionStorage.getItem(SESSION_TOKEN_KEY);
}

export function getSessionUser(): Omit<SessionData, 'token'> | null {
  const user = sessionStorage.getItem(SESSION_USER_KEY);
  return user ? JSON.parse(user) : null;
}

export function clearSessionToken(): void {
  sessionStorage.removeItem(SESSION_TOKEN_KEY);
  sessionStorage.removeItem(SESSION_USER_KEY);
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  clearAuthToken();

  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
    refreshTimeout = null;
  }
}

export function isSessionValid(): boolean {
  const token = getSessionToken();
  const user = getSessionUser();

  if (!token || !user) {
    return false;
  }

  // Check if token is expired
  return user.expiresAt > Date.now();
}

// API request helpers
export const api = {
  get: <T = any>(url: string, config?: AxiosRequestConfig) =>
    getApiClient().get<T>(url, config),
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    getApiClient().post<T>(url, data, config),
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    getApiClient().put<T>(url, data, config),
  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    getApiClient().patch<T>(url, data, config),
  delete: <T = any>(url: string, config?: AxiosRequestConfig) =>
    getApiClient().delete<T>(url, config),
};

// Helper to get API configuration
export function getApiConfig() {
  return {
    baseURL: getBaseUrl(),
  };
}

/**
 * Ensure API client is initialized and authenticated
 * Performs service user login on first call
 */
export async function ensureApiAuthenticated(): Promise<void> {
  if (initPromise) {
    return initPromise;
  }

  initPromise = (async () => {
    const client = getApiClient();
    const serviceUsername = getServiceUsername();
    const servicePassword = getServicePassword();
    const authMode = getFrontendAuthMode();
    const storedUser = getSessionUser();
    
    // Check if we already have a valid session
    const token = getSessionToken();
    if (token && isSessionValid()) {
      // In Entra or hybrid mode, validate persisted Entra-backed sessions once on startup.
      // Deploys/restarts can leave the browser holding a token the backend no longer accepts.
      if ((authMode === 'entra' || authMode === 'hybrid') && storedUser?.authProvider === 'entra') {
        const refreshedToken = await refreshAccessToken();
        if (refreshedToken) {
          return;
        }
      } else {
        return;
      }
    }

    if ((authMode === 'entra' || authMode === 'hybrid') && storedUser?.authProvider === 'entra') {
      const recoveredToken = await refreshAccessToken();
      if (recoveredToken) {
        return;
      }
    }

    // In Entra mode, interactive sign-in is required from the login page if silent recovery fails.
    if (authMode === 'entra') {
      initPromise = null;
      return;
    }

    // Try to login as service user from explicit env config.
    try {
      if (!serviceUsername || !servicePassword) {
        throw new Error('Missing VITE_SERVICE_USERNAME or VITE_SERVICE_PASSWORD');
      }

      const response = await client.post<any>('/auth/login', {
        username: serviceUsername,
        password: servicePassword,
      });

      const accessToken = response.data?.access_token;
      const refreshToken = response.data?.refresh_token;
      const user = response.data?.user;

      if (accessToken && user?.id) {
        // Access token ~30 minutes; refresh ~8 hours (use buffers)
        const expiresAt = Date.now() + 25 * 60 * 1000;
        const refreshExpiresAt = Date.now() + 7.5 * 60 * 60 * 1000;
        setSessionToken({
          token: accessToken,
          userId: user.id,
          username: user.username,
          expiresAt,
          authProvider: 'local',
          refreshToken,
          refreshExpiresAt,
        });
      }
    } catch (error) {
      console.error('Failed to authenticate with service account:', error);
      initPromise = null;
      throw error;
    }
  })();

  return initPromise;
}

/**
 * Attempt to refresh the access token using the stored refresh token.
 * Returns the new access token or null on failure.
 */
async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const stored = getSessionUser();
    try {
      const apiKey = getConfiguredApiKey();
      const refreshToken = stored?.refreshToken || localStorage.getItem('refresh_token');
      if (!refreshToken) throw new Error('No refresh token');

      const response = await axios.post<any>(
        `${getBaseUrl()}/v1/auth/refresh`,
        { refresh_token: refreshToken },
        {
          timeout: 30000,
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': apiKey,
          },
        },
      );

      const accessToken = response.data?.access_token;
      const user = response.data?.user || stored;

      const userId = user?.userId || user?.id;
      if (accessToken && userId) {
        const expiresAt = Date.now() + 25 * 60 * 1000;
        const refreshExpiresAt = stored?.refreshExpiresAt || (Date.now() + 7.5 * 60 * 60 * 1000);
        setSessionToken({
          token: accessToken,
          userId,
          username: user.username,
          expiresAt,
          authProvider: stored?.authProvider,
          refreshToken,
          refreshExpiresAt,
        });
        return accessToken;
      }
    } catch (err) {
      console.error('Token refresh failed:', err);

      // Entra sessions can recover by silently renewing with MSAL and re-exchanging.
      if (stored?.authProvider === 'entra') {
        const recovered = await tryRecoverEntraSession(stored);
        if (recovered) {
          return recovered;
        }
      }

      clearSessionToken();
      initPromise = null;
      return null;
    } finally {
      refreshPromise = null;
    }
    return null;
  })();

  return refreshPromise;
}

async function tryRecoverEntraSession(
  storedUser: Omit<SessionData, 'token'>,
): Promise<string | null> {
  try {
    const apiKey = getConfiguredApiKey();
    const { acquireEntraAccessTokenSilent } = await import('./entraAuth');
    const entraAccessToken = await acquireEntraAccessTokenSilent();
    if (!entraAccessToken) {
      return null;
    }

    const response = await axios.post<any>(
      `${getBaseUrl()}/v1/auth/entra/exchange`,
      { access_token: entraAccessToken },
      {
        timeout: 30000,
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey,
        },
      },
    );

    const accessToken = response.data?.access_token;
    const refreshToken = response.data?.refresh_token;
    const user = response.data?.user;
    if (!accessToken || !user?.id) {
      return null;
    }

    const expiresAt = Date.now() + 25 * 60 * 1000;
    const refreshExpiresAt = Date.now() + 7.5 * 60 * 60 * 1000;
    setSessionToken({
      token: accessToken,
      userId: user.id,
      username: user.username,
      expiresAt,
      authProvider: 'entra',
      refreshToken,
      refreshExpiresAt,
    });

    return accessToken;
  } catch (err) {
    console.error('Entra silent recovery failed:', err, storedUser?.username);
    return null;
  }
}

function scheduleRefresh(session: SessionData) {
  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
  }
  if (!session.expiresAt) return;

  // Refresh 5 minutes before expiry (or at least in 1 minute)
  const delay = Math.max(session.expiresAt - Date.now() - 5 * 60 * 1000, 60 * 1000);
  refreshTimeout = setTimeout(() => {
    refreshAccessToken().catch(() => {
      /* handled in refresh */
    });
  }, delay);
}

export default getApiClient;
